/* ============================================================================
   PALETA — una sola definición para toda la aplicación.

   CRITERIOS, EN ORDEN DE PRIORIDAD

   1. Convención aeronáutica. Los significados de color en cabina no se
      inventan: verde es parámetro normal, ámbar es precaución, rojo es límite
      excedido, cian es dato calculado, magenta es lo que el piloto eligió.
      Un piloto los lee sin pensar, y cambiarlos costaría más de lo que
      cualquier mejora estética podría aportar.

      Nota: son convenciones de la industria, no la paleta de ningún producto
      comercial concreto. Copiar los tonos exactos, la tipografía y los iconos
      de una aplicación comercial sería replicar su identidad.

   2. Vista en cabina. Fondo oscuro azulado en vez de negro puro: el negro
      absoluto contra texto claro produce halo alrededor de las letras y cansa
      en vuelo nocturno. El texto tampoco es blanco puro por lo mismo.

   3. Contraste medido, no estimado. Todos los colores de texto superan 4,5:1
      contra el fondo y contra el panel — el mínimo AA de WCAG para texto
      normal. Los valores están comprobados por `paleta.test.js`, que falla si
      alguien introduce un tono que no llega.

   4. Visión de color. Cerca del 8 % de los hombres tiene alguna forma de
      dicromatismo, y en esta industria eso es mucha gente. Ámbar y rojo son
      justamente el par que más se confunde: en la paleta anterior su
      separación bajo deuteranopia era de 57 unidades. El amarillo elegido la
      lleva a 89.

      Aun así, el color NUNCA es el único portador de información: cada estado
      lleva su etiqueta escrita (ACEPTABLE, GRAVE, INACEPTABLE) y su valor.
      Si algún día el color se pierde, el dato sigue ahí.
   ========================================================================== */

export const PALETA = {
  /** Fondo general: azulado muy oscuro, no negro. */
  bg: "#0C1116",
  /** Superficie de panel, un escalón por encima del fondo. */
  panel: "#161D25",
  /** Superficie interior: campos, filas alternas. */
  panelAlt: "#1D2732",
  /** Líneas de separación. Suficiente para estructurar, sin dibujar rejas. */
  hairline: "#2B3641",

  /** Texto principal. No blanco puro: reduce el halo sobre fondo oscuro. */
  text: "#E6EDF3",
  /** Texto secundario. Supera 4,5:1: lo secundario no es lo ilegible. */
  muted: "#93A3B3",

  /** Dato calculado por la aplicación. */
  cyan: "#5BD1D7",
  /** Encabezados y acentos de sección. */
  amber: "#F2A93B",
  /** Elegido por el piloto: nivel de crucero, senda vertical, carga de tanque. */
  magenta: "#FF3EA5",

  /** Parámetro dentro de lo normal. */
  ok: "#4CAF7D",
  /** Precaución. Amarillo, no naranja: se separa mejor del rojo. */
  caution: "#FFD23F",
  /** Límite excedido. */
  danger: "#E4573D",
};

/* ============================================================================
   MARCA — los colores del ícono de HeliCalc, medidos del PNG píxel por píxel.

   Reparto real en el ícono: gris 85,0 % · verde azulado 8,2 % · naranja 2,4 %
   · azul marino 1,4 %.

   POR QUÉ LA MARCA NO REEMPLAZA A LA PALETA DE ARRIBA

   El naranja del ícono (#D97B31) cae en la banda del ámbar de precaución. Esta
   aplicación ya movió la precaución al amarillo #FFD23F justamente porque el
   par naranja/rojo es el que peor se separa bajo dicromatismo — con el naranja
   la separación era 57, con el amarillo es 89. Reintroducir el naranja como
   color de estado desharía esa decisión, que está medida y probada.

   Así que la marca entra donde no compite con el significado:

     · `teal` es el color de lo que se toca y de la estructura del panel.
       Ese hueco existía: hasta ahora los grupos de navegación usaban ámbar,
       que es el color de "presta atención". Un menú no es una precaución.
     · `naranja` queda para el logotipo y nada más.
     · `navy` es el fondo de las superficies que muestran un valor calculado.
     · `carbon` es el gris del ícono; sirve de borde de bisel.

   `tealPantalla` es el mismo verde azulado aclarado hasta pasar 4,5:1 sobre
   el fondo y sobre el panel. El de marca, sin aclarar, no llega: se usa para
   trazos de icono y filetes, no para texto. `marca.test.js` lo comprueba.
   ========================================================================== */
export const MARCA = {
  /** Gris del ícono. Bisel y bordes de panel. */
  carbon: "#313131",
  /** Verde azulado de la H. Identidad; en trazos y filetes, no en texto. */
  teal: "#5C8886",
  /** El mismo verde azulado, aclarado para poder leerlo en pantalla. */
  tealPantalla: "#8FBFBC",
  /** Naranja del arco. Solo logotipo. */
  naranja: "#D97B31",
  /** Azul marino del helicóptero. Fondo de un valor calculado. */
  navy: "#141B40",
  /** Reflejo del vidrio: superficie elevada y borde activo. */
  navyAlto: "#293670",
};

/**
 * Tipografía.
 *
 * Tres familias con un trabajo cada una:
 *
 *   · Barlow Condensed para títulos y etiquetas. Condensada, así una etiqueta
 *     larga entra sin abreviarse; y las abreviaturas mal entendidas son un
 *     problema conocido en cabina.
 *
 *   · JetBrains Mono para TODO número. Ancho fijo, así una columna de cifras
 *     se lee alineada, y —lo que más importa— distingue el cero del O y el uno
 *     del l y del I. En una matrícula o un número de parte eso no es un
 *     detalle tipográfico.
 *
 *   · Inter para texto corrido.
 */
export const TIPOGRAFIA = {
  titulos: "'Barlow Condensed', sans-serif",
  datos: "'JetBrains Mono', monospace",
  texto: "'Inter', sans-serif",
};

/** Color de cada nivel de riesgo, con el mismo criterio de convención. */
export const COLOR_NIVEL = {
  ACEPTABLE: PALETA.ok,
  TOLERABLE: PALETA.caution,
  INTOLERABLE: PALETA.danger,
  MEDIO: "#4C8DD6",
  GRAVE: PALETA.caution,
  INACEPTABLE: "#E8842D",
  "INACEPTABLE+": PALETA.danger,
};
